import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Globe, Star } from "lucide-react";

interface Language {
  id: string;
  code: string;
  name: string;
  native_name: string;
  is_active: boolean;
  is_default: boolean;
  flag_emoji: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const AdminLanguages = () => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLanguage, setEditingLanguage] = useState<Language | null>(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    native_name: "",
    is_active: true,
    is_default: false,
    flag_emoji: "",
    sort_order: 0,
  });

  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchLanguages();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
      return;
    }

    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (!adminUser) {
      navigate('/admin/login');
    }
  };

  const fetchLanguages = async () => {
    try {
      const { data, error } = await supabase
        .from('languages')
        .select('*')
        .order('sort_order');

      if (error) {
        console.error('Error fetching languages:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los idiomas",
          variant: "destructive",
        });
        return;
      }

      setLanguages(data || []);
    } catch (error) {
      console.error('Error fetching languages:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los idiomas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      name: "",
      native_name: "",
      is_active: true,
      is_default: false,
      flag_emoji: "",
      sort_order: languages.length,
    });
    setEditingLanguage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // If setting as default, update other languages first and sync with settings
      if (formData.is_default) {
        await supabase
          .from('languages')
          .update({ is_default: false })
          .neq('id', editingLanguage?.id || '');
        
        // Update translation settings to match
        await supabase
          .from('translation_settings')
          .update({ default_language: formData.code })
          .limit(1);
      }

      let error;
      if (editingLanguage) {
        const { error: updateError } = await supabase
          .from('languages')
          .update(formData)
          .eq('id', editingLanguage.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('languages')
          .insert([formData]);
        error = insertError;
      }

      if (error) {
        console.error('Error saving language:', error);
        toast({
          title: "Error",
          description: `No se pudo ${editingLanguage ? 'actualizar' : 'crear'} el idioma`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Éxito",
        description: `Idioma ${editingLanguage ? 'actualizado' : 'creado'} exitosamente`,
      });

      setIsDialogOpen(false);
      resetForm();
      fetchLanguages();
    } catch (error) {
      console.error('Error saving language:', error);
      toast({
        title: "Error",
        description: `No se pudo ${editingLanguage ? 'actualizar' : 'crear'} el idioma`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (language: Language) => {
    setEditingLanguage(language);
    setFormData({
      code: language.code,
      name: language.name,
      native_name: language.native_name,
      is_active: language.is_active,
      is_default: language.is_default,
      flag_emoji: language.flag_emoji || "",
      sort_order: language.sort_order,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Estás seguro de que quieres eliminar este idioma?")) return;

    try {
      const { error } = await supabase
        .from('languages')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting language:', error);
        toast({
          title: "Error",
          description: "No se pudo eliminar el idioma",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Éxito",
        description: "Idioma eliminado exitosamente",
      });

      fetchLanguages();
    } catch (error) {
      console.error('Error deleting language:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el idioma",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Gestión de Idiomas</h1>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Idioma
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingLanguage ? "Editar Idioma" : "Nuevo Idioma"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Código del Idioma *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="es, en, fr..."
                      required
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="flag_emoji">Emoji de Bandera</Label>
                    <Input
                      id="flag_emoji"
                      value={formData.flag_emoji}
                      onChange={(e) => setFormData(prev => ({ ...prev, flag_emoji: e.target.value }))}
                      placeholder="🇪🇸, 🇺🇸, 🇫🇷..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre en Inglés *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Spanish, English, French..."
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="native_name">Nombre Nativo *</Label>
                    <Input
                      id="native_name"
                      value={formData.native_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, native_name: e.target.value }))}
                      placeholder="Español, English, Français..."
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sort_order">Orden de Clasificación</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                    min="0"
                  />
                </div>

                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                    />
                    <Label htmlFor="is_active">Activo</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_default"
                      checked={formData.is_default}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked }))}
                    />
                    <Label htmlFor="is_default">Idioma por Defecto</Label>
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? "Guardando..." : editingLanguage ? "Actualizar" : "Crear"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {languages.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Globe className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No hay idiomas configurados</h3>
              <p className="text-muted-foreground text-center mb-6">
                Agrega idiomas para habilitar el sistema de traducción.
              </p>
              <Button onClick={() => {resetForm(); setIsDialogOpen(true)}}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Primer Idioma
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {languages.map((language) => (
              <Card key={language.id} className="hover:shadow-lg transition-all duration-300">
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{language.flag_emoji}</span>
                      <div>
                        <CardTitle className="text-lg">{language.native_name}</CardTitle>
                        <p className="text-sm text-muted-foreground">{language.name}</p>
                      </div>
                    </div>
                    {language.is_default && (
                      <Badge variant="default">
                        <Star className="h-3 w-3 mr-1" />
                        Por Defecto
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {language.code.toUpperCase()}
                    </Badge>
                    <Badge variant={language.is_active ? "outline" : "secondary"}>
                      {language.is_active ? "Activo" : "Inactivo"}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(language)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(language.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLanguages;